export interface PopupResultSuccess {
  type: 'oauth:success';
  provider: string;
  channelId: string;
  account?: { id: string | number; login?: string; avatarUrl?: string };
}

export interface PopupResultTicket {
  type: 'oauth:ticket';
  provider: string;
  ticketId: string;
  channelId: string;
}

export interface PopupResultPending {
  type: 'oauth:pending';
  provider: string;
  ticketId: string;
  channelId: string;
  tenantId: string;
  pendingToken: string;
}

export interface PopupResultError {
  type: 'oauth:error';
  channelId: string;
  reason: string;
  code?: string;
  message?: string;
  translationKey?: string;
  translationParams?: Record<string, string | number>;
}

// Error types for better error handling
export enum OAuthErrorCode {
  POPUP_BLOCKED = 'POPUP_BLOCKED',
  TIMEOUT = 'TIMEOUT',
  INVALID_ORIGIN = 'INVALID_ORIGIN',
  INVALID_MESSAGE = 'INVALID_MESSAGE',
  CHANNEL_MISMATCH = 'CHANNEL_MISMATCH',
  UNKNOWN_ERROR = 'UNKNOWN_ERROR',
}

export interface OAuthError extends Error {
  code: OAuthErrorCode;
  channelId?: string;
  originalError?: unknown;
}

// Constants for better maintainability
const DEFAULT_POPUP_TIMEOUT_MS = 90_000; // 90 seconds
const POPUP_DIMENSIONS = {
  width: 600,
  height: 750,
} as const;

// Get timeout from environment variable if available
function getPopupTimeout(): number {
  const envTimeout = process.env.NEXT_PUBLIC_OAUTH_POPUP_TIMEOUT_MS;
  if (envTimeout) {
    const parsed = parseInt(envTimeout, 10);
    if (!isNaN(parsed) && parsed > 0) {
      return parsed;
    }
  }
  return DEFAULT_POPUP_TIMEOUT_MS;
}

// Type guards for better type safety
function isPopupResultSuccess(data: unknown): data is PopupResultSuccess {
  return (
    typeof data === 'object' &&
    data !== null &&
    'type' in data &&
    (data as { type: unknown }).type === 'oauth:success' &&
    'channelId' in data &&
    typeof (data as { channelId: unknown }).channelId === 'string'
  );
}

function isPopupResultTicket(data: unknown): data is PopupResultTicket {
  return (
    typeof data === 'object' &&
    data !== null &&
    'type' in data &&
    (data as { type: unknown }).type === 'oauth:ticket' &&
    'channelId' in data &&
    typeof (data as { channelId: unknown }).channelId === 'string' &&
    'ticketId' in data &&
    typeof (data as { ticketId: unknown }).ticketId === 'string' &&
    'provider' in data &&
    typeof (data as { provider: unknown }).provider === 'string'
  );
}

function isPopupResultPending(data: unknown): data is PopupResultPending {
  return (
    typeof data === 'object' &&
    data !== null &&
    'type' in data &&
    (data as { type: unknown }).type === 'oauth:pending' &&
    'channelId' in data &&
    typeof (data as { channelId: unknown }).channelId === 'string' &&
    'provider' in data &&
    typeof (data as { provider: unknown }).provider === 'string' &&
    'tenantId' in data &&
    typeof (data as { tenantId: unknown }).tenantId === 'string' &&
    'pendingToken' in data &&
    typeof (data as { pendingToken: unknown }).pendingToken === 'string'
  );
}

function isPopupResultError(data: unknown): data is PopupResultError {
  return (
    typeof data === 'object' &&
    data !== null &&
    'type' in data &&
    (data as { type: unknown }).type === 'oauth:error' &&
    'channelId' in data &&
    typeof (data as { channelId: unknown }).channelId === 'string' &&
    'reason' in data &&
    typeof (data as { reason: unknown }).reason === 'string'
  );
}

function isPopupResult(
  data: unknown
): data is
  | PopupResultSuccess
  | PopupResultTicket
  | PopupResultPending
  | PopupResultError {
  return (
    isPopupResultSuccess(data) ||
    isPopupResultTicket(data) ||
    isPopupResultPending(data) ||
    isPopupResultError(data)
  );
}

// Error creation utilities
function createOAuthError(
  code: OAuthErrorCode,
  message: string,
  channelId?: string,
  originalError?: unknown
): OAuthError {
  const error = new Error(message) as OAuthError;
  error.code = code;
  if (channelId !== undefined) {
    error.channelId = channelId;
  }
  if (originalError !== undefined) {
    error.originalError = originalError;
  }
  return error;
}

function createPopupError(
  code: OAuthErrorCode,
  reason: string,
  channelId: string,
  message?: string,
  translationKey?: string,
  translationParams?: Record<string, string | number>
): PopupResultError {
  return {
    type: 'oauth:error',
    channelId,
    reason,
    code,
    ...(message && { message }),
    ...(translationKey && { translationKey }),
    ...(translationParams && { translationParams }),
  };
}

type SupportedProvider = 'github' | 'microsoft' | 'linear' | 'notion';

export async function openProviderPopup(params: {
  provider: SupportedProvider;
  channelId?: string;
  allowedOrigins?: string[];
  timeoutMs?: number;
  /** Additional OAuth scopes to request (for scope upgrades). */
  scopes?: string[] | undefined;
  onSuccess: (p: PopupResultSuccess) => void;
  onTicket: (p: PopupResultTicket) => void;
  /**
   * Best-effort admin-consent fallback (bridge `oauth:pending` message).
   * Optional: callers without a pending surface receive a graceful synthesized
   * `onError` instead — never the raw pending payload, never a timeout hang.
   */
  onPending?: (p: PopupResultPending) => void;
  onError: (p: PopupResultError) => void;
}) {
  const channelId = params.channelId ?? crypto.randomUUID();
  const timeoutMs = params.timeoutMs ?? getPopupTimeout();

  const startUrl = new URL(
    `/api/integrations/${params.provider}/oauth/start`,
    window.location.origin
  );
  startUrl.searchParams.set('channelId', channelId);
  if (params.scopes && params.scopes.length > 0) {
    startUrl.searchParams.set('scopes', params.scopes.join(','));
  }

  const w = window.open(
    startUrl.toString(),
    'oauth',
    `width=${POPUP_DIMENSIONS.width},height=${POPUP_DIMENSIONS.height},menubar=no,toolbar=no,location=no,status=no`
  );
  if (!w) {
    throw createOAuthError(
      OAuthErrorCode.POPUP_BLOCKED,
      'Popup was blocked by the browser. Please allow popups for this site and try again.',
      channelId
    );
  }

  const timeout = setTimeout(() => {
    window.removeEventListener('message', onMessage);
    try {
      w.close();
    } catch {}
    params.onError(
      createPopupError(
        OAuthErrorCode.TIMEOUT,
        'timeout',
        channelId,
        `OAuth flow timed out after ${timeoutMs / 1000} seconds. Please try again.`,
        'oauth.timeout'
      )
    );
  }, timeoutMs);

  function onMessage(e: MessageEvent) {
    // Only accept messages from the popup we opened
    if (e.source !== w) {
      return;
    }
    // Validate origin
    if (params.allowedOrigins && params.allowedOrigins.length > 0) {
      if (!params.allowedOrigins.includes(e.origin)) {
        params.onError(
          createPopupError(
            OAuthErrorCode.INVALID_ORIGIN,
            'invalid_origin',
            channelId,
            `Message from unauthorized origin: ${e.origin}`,
            'oauth.invalidOrigin',
            { origin: e.origin }
          )
        );
        return;
      }
    }

    const data = e.data;
    // Ignore unrelated messages that don't match our expected shape
    if (!isPopupResult(data)) {
      return;
    }

    if (data.channelId !== channelId) {
      params.onError(
        createPopupError(
          OAuthErrorCode.CHANNEL_MISMATCH,
          'channel_mismatch',
          channelId,
          `Channel ID mismatch: expected ${channelId}, got ${data.channelId}`,
          'oauth.channelMismatch',
          { expected: channelId, actual: data.channelId }
        )
      );
      return;
    }

    clearTimeout(timeout);
    window.removeEventListener('message', onMessage);

    if (isPopupResultSuccess(data)) {
      params.onSuccess(data);
    } else if (isPopupResultTicket(data)) {
      params.onTicket(data);
    } else if (isPopupResultPending(data)) {
      if (params.onPending) {
        params.onPending(data);
      } else {
        // Caller has no pending surface — fail gracefully right away instead
        // of leaking the mis-shaped pending payload into onError or hanging
        // until the popup timeout fires.
        params.onError(
          createPopupError(
            OAuthErrorCode.UNKNOWN_ERROR,
            'pending_unsupported',
            channelId,
            undefined,
            'oauth.genericError'
          )
        );
      }
    } else {
      params.onError(data);
    }
  }

  window.addEventListener('message', onMessage);
  return { channelId };
}

/**
 * Map of OAuth error codes to translation keys.
 * Used as a fallback when error doesn't include a translationKey.
 */
const OAUTH_ERROR_TRANSLATION_MAP: Record<
  string,
  {
    key: string;
    params?: (error: PopupResultError) => Record<string, string | number>;
  }
> = {
  [OAuthErrorCode.POPUP_BLOCKED]: { key: 'oauth.popupBlocked' },
  [OAuthErrorCode.TIMEOUT]: { key: 'oauth.timeout' },
  [OAuthErrorCode.INVALID_ORIGIN]: {
    key: 'oauth.invalidOrigin',
    params: error => ({ origin: error.message || 'unknown' }),
  },
  [OAuthErrorCode.INVALID_MESSAGE]: { key: 'oauth.invalidMessage' },
  [OAuthErrorCode.CHANNEL_MISMATCH]: {
    key: 'oauth.channelMismatch',
    params: error => ({
      expected: error.channelId,
      actual: error.message || 'unknown',
    }),
  },
};

/**
 * Utility function to get user-friendly error message from OAuth error object.
 * Uses translation keys from errors.json.
 */
export function getOAuthErrorMessage(
  error: PopupResultError,
  t: (key: string, params?: Record<string, string | number>) => string
): string {
  // Priority 1: Use translationKey if provided
  if (error.translationKey) {
    return error.translationParams
      ? t(error.translationKey, error.translationParams)
      : t(error.translationKey);
  }

  // Priority 2: Use error code mapping
  if (error.code) {
    const mapping = OAUTH_ERROR_TRANSLATION_MAP[error.code];
    if (mapping) {
      const params = mapping.params?.(error);
      return params ? t(mapping.key, params) : t(mapping.key);
    }
  }

  // Priority 3: Fallback to error message or generic error
  return error.message || t('oauth.genericError');
}
